import React, { useState, useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { tenantAPI } from '../services/garageApi';
import { formatTenantStatus, isTenantInTrial, getTrialDaysRemaining, isSubscriptionActive } from '../utils/tenantUtils';
import { AlertCircle } from 'lucide-react';

const TenantManagement = () => {
  const { tenant: contextTenant, loading: contextLoading } = useTenant();
  const { user, isTenantAdmin } = useAuth();
  
  const [tenant, setTenant] = useState(null);
  const [settings, setSettings] = useState({});
  const [features, setFeatures] = useState([]);
  const [usage, setUsage] = useState({});
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const [formData, setFormData] = useState({});
  const [settingsData, setSettingsData] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    const loadTenantData = async () => {
      try {
        setLoading(true);
        setError('');

        // Use tenant from context if available, otherwise try to get from user
        let tenantData = contextTenant;
        
        if (!tenantData && user?.tenant_id) {
          // Try to fetch tenant data if we have tenant_id
          try {
            const response = await tenantAPI.getCurrent();
            tenantData = response.data?.tenant || response.data;
          } catch (err) {
            console.error('Error fetching tenant:', err);
          }
        }

        if (tenantData) {
          setTenant(tenantData);
          
          // Extract settings and features from tenant object
          setSettings(tenantData.settings || {});
          setFeatures(tenantData.features || []);
          
          // Calculate usage from tenant relationships if available
          setUsage({
            user_count: tenantData.users_count || tenantData.users?.length || 0,
            location_count: tenantData.locations_count || tenantData.locations?.length || 0
          });
        } else {
          setError('No tenant information available. Please contact your administrator.');
        }
      } catch (err) {
        console.error('Error loading tenant data:', err);
        setError('Failed to load tenant information');
      } finally {
        setLoading(false);
      }
    };

    if (!contextLoading) {
      loadTenantData();
    }
  }, [contextTenant, contextLoading, user]);

  const handleEdit = () => {
    if (!tenant) return;
    setFormData({
      name: tenant.name || '',
      email: tenant.email || '',
      phone: tenant.phone || '',
      address: tenant.address || ''
    });
    setIsEditing(true);
  };

  const handleSettingsEdit = () => {
    setSettingsData({
      currency: settings.currency || 'USD',
      timezone: settings.timezone || 'UTC',
      language: settings.language || 'en',
      tax_rate: settings.tax_rate || 0.15,
      app_name: settings.app_name || tenant?.name
    });
    setIsEditingSettings(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await tenantAPI.update(formData);
      setTenant(prev => ({ ...prev, ...formData }));
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating tenant:', error);
      alert('Failed to update tenant information');
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      await tenantAPI.updateSettings(settingsData);
      setSettings(prev => ({ ...prev, ...settingsData }));
      setIsEditingSettings(false);
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Failed to update settings');
    }
  };

  const canAddMoreUsers = () => {
    if (!tenant) return false;
    if (tenant.user_limit === -1) return true;
    return (usage.user_count || 0) < tenant.user_limit;
  };

  const canAddMoreLocations = () => {
    if (!tenant) return false;
    if (tenant.location_limit === -1) return true;
    return (usage.location_count || 0) < tenant.location_limit;
  };

  if (loading || contextLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading tenant information...</span>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <p className="text-lg font-medium">{error || 'No tenant information available'}</p>
          </div>
          <p className="mt-2 text-gray-600">
            {user?.super_admin 
              ? 'As a super admin, you need to select a tenant context to view tenant management.'
              : 'Please contact your administrator if you believe this is an error.'}
          </p>
        </div>
      </div>
    );
  }

  const statusInfo = formatTenantStatus(tenant.status);
  const trialDays = getTrialDaysRemaining(tenant);
  const subscriptionActive = isSubscriptionActive(tenant);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tenant Management</h1>
              <p className="text-gray-600">Manage your organization's settings and subscription</p>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                statusInfo.color === 'green' ? 'bg-green-100 text-green-800' :
                statusInfo.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {statusInfo.label}
              </span>
              {isTenantInTrial(tenant) && trialDays > 0 && (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  {trialDays} days left
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
          {/* Tenant Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
                {isTenantAdmin && (
                  <button
                    onClick={handleEdit}
                    className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                  >
                    Edit
                  </button>
                )}
              </div>

              {!isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Organization Name</label>
                    <p className="mt-1 text-sm text-gray-900">{tenant.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{tenant.email || 'Not set'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <p className="mt-1 text-sm text-gray-900">{tenant.phone || 'Not set'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Address</label>
                    <p className="mt-1 text-sm text-gray-900">{tenant.address || 'Not set'}</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Organization Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Address</label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                    >
                      Save Changes
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Settings */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Application Settings</h2>
                {isTenantAdmin && (
                  <button
                    onClick={handleSettingsEdit}
                    className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                  >
                    Edit
                  </button>
                )}
              </div>

              {!isEditingSettings ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Currency</label>
                    <p className="mt-1 text-sm text-gray-900">{settings.currency || 'USD'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Timezone</label>
                    <p className="mt-1 text-sm text-gray-900">{settings.timezone || 'UTC'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Language</label>
                    <p className="mt-1 text-sm text-gray-900">{settings.language || 'en'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tax Rate</label>
                    <p className="mt-1 text-sm text-gray-900">{(settings.tax_rate || 0.15) * 100}%</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSettingsSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Currency</label>
                      <select
                        value={settingsData.currency}
                        onChange={(e) => setSettingsData({ ...settingsData, currency: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="CAD">CAD</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Timezone</label>
                      <select
                        value={settingsData.timezone}
                        onChange={(e) => setSettingsData({ ...settingsData, timezone: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="UTC">UTC</option>
                        <option value="America/New_York">Eastern Time</option>
                        <option value="America/Chicago">Central Time</option>
                        <option value="America/Denver">Mountain Time</option>
                        <option value="America/Los_Angeles">Pacific Time</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Language</label>
                      <select
                        value={settingsData.language}
                        onChange={(e) => setSettingsData({ ...settingsData, language: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="en">English</option>
                        <option value="es">Spanish</option>
                        <option value="fr">French</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Tax Rate (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={(settingsData.tax_rate || 0.15) * 100}
                        onChange={(e) => setSettingsData({ ...settingsData, tax_rate: parseFloat(e.target.value) / 100 })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                    >
                      Save Settings
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditingSettings(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Subscription Info */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-4">Subscription</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-blue-700">Current Plan</p>
                  <p className="text-lg font-semibold text-blue-900 capitalize">{tenant.subscription_plan || 'N/A'}</p>
                </div>
                {subscriptionActive ? (
                  <div className="text-sm text-blue-700">
                    <p>Status: <span className="font-medium text-green-600">Active</span></p>
                  </div>
                ) : (
                  <div className="text-sm text-red-700">
                    <p>Status: <span className="font-medium text-red-600">Inactive</span></p>
                  </div>
                )}
                {tenant.subscription_ends_at && (
                  <div>
                    <p className="text-sm text-blue-700">Expires</p>
                    <p className="text-sm font-medium text-blue-900">
                      {new Date(tenant.subscription_ends_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Usage Limits */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Limits</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Users</span>
                    <span className="font-medium text-gray-900">
                      {usage.user_count || 0} / {tenant.user_limit === -1 ? '∞' : tenant.user_limit}
                    </span>
                  </div>
                  <div className="mt-2 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ 
                        width: `${tenant.user_limit === -1 ? 100 : Math.min(100, ((usage.user_count || 0) / tenant.user_limit) * 100)}%` 
                      }}
                    ></div>
                  </div>
                  {!canAddMoreUsers() && (
                    <p className="text-xs text-red-600 mt-1">Limit reached</p>
                  )}
                </div>

                <div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Locations</span>
                    <span className="font-medium text-gray-900">
                      {usage.location_count || 0} / {tenant.location_limit === -1 ? '∞' : tenant.location_limit}
                    </span>
                  </div>
                  <div className="mt-2 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ 
                        width: `${tenant.location_limit === -1 ? 100 : Math.min(100, ((usage.location_count || 0) / tenant.location_limit) * 100)}%` 
                      }}
                    ></div>
                  </div>
                  {!canAddMoreLocations() && (
                    <p className="text-xs text-red-600 mt-1">Limit reached</p>
                  )}
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Features</h3>
              <div className="space-y-2">
                {features && features.length > 0 ? (
                  features.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-gray-700 capitalize">{typeof feature === 'string' ? feature.replace(/_/g, ' ') : feature}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No features available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantManagement;
